/// <amd-module name="VSS/Features/MarkdownItKatex/MarkdownItKatex" />
/* This code is copied from https://github.com/waylonflinn/markdown-it-katex and modified to support AMD */
/* Process inline math */
/*
Like markdown-it-simplemath, this is a stripped down, simplified version of:
https://github.com/runarberg/markdown-it-math
It differs in that it takes (a subset of) LaTeX as input and relies on KaTeX
for rendering output.
*/
/*jslint node: true */
import * as katex from "katex";
import * as MarkdownIt from "markdown-it";

interface IState {
    md: MarkdownIt.MarkdownIt;
    src: string;
    tokens: MarkdownIt.Token[];
    [key: string]: any;
}

export interface KatexOptions {
    displayMode?: boolean;
    throwOnError?: boolean;
}

export class MarkdownItKatex {
    private _options: KatexOptions;
    constructor(md: MarkdownIt.MarkdownIt, opt?: KatexOptions) {
        // Default options
        this._options = opt || {};
        // set KaTeX as the renderer for markdown-it-simplemath
        const katexInline = (latex: string) => {
            this._options.displayMode = false;
            try {
                return katex.renderToString(latex, this._options);
            } catch (error) {
                if (this._options.throwOnError) {
                    console.log(error);
                }
                return latex;
            }
        };
        const inlineRenderer = (tokens: MarkdownIt.Token[], idx: number) => {
            return katexInline(tokens[idx].content);
        };
        const katexBlock = (latex: string) => {
            this._options.displayMode = true;
            try {
                return "<p>" + katex.renderToString(latex, this._options) + "</p>";
            } catch (error) {
                if (this._options.throwOnError) {
                    console.log(error);
                }
                return latex;
            }
        };
        const blockRenderer = (tokens: MarkdownIt.Token[], idx: number) => {
            return katexBlock(tokens[idx].content) + "\n";
        };
        md.inline.ruler.after("escape", "math_inline", this._mathInline);
        (md.block.ruler as any).after("blockquote", "math_block", this._mathBlock, {
            alt: ["paragraph", "reference", "blockquote", "list"]
        });
        md.renderer.rules.math_inline = inlineRenderer;
        md.renderer.rules.math_block = blockRenderer;
    }

    private _isValidDelim = (state: any, pos: any): any => {
        let prevChar: any,
            nextChar: any,
            max = state.posMax,
            can_open = true,
            can_close = true;
        prevChar = pos > 0 ? state.src.charCodeAt(pos - 1) : -1;
        nextChar = pos + 1 <= max ? state.src.charCodeAt(pos + 1) : -1;
        // Check non-whitespace conditions for opening and closing, and
        // check that closing delimeter isn't followed by a number
        if (prevChar === 0x20 /* " " */ || prevChar === 0x09 /* \t */ || (nextChar >= 0x30 /* "0" */ && nextChar <= 0x39) /* "9" */) {
            can_close = false;
        }
        if (nextChar === 0x20 /* " " */ || nextChar === 0x09 /* \t */) {
            can_open = false;
        }
        return {
            can_open: can_open,
            can_close: can_close
        };
    };
    private _mathInline = (state: IState, silent: boolean): boolean => {
        let start: any, match: any, token: MarkdownIt.Token, res: any, pos: any;
        if (state.src[state.pos] !== "$") {
            return false;
        }
        res = this._isValidDelim(state, state.pos);
        if (!res.can_open) {
            if (!silent) {
                state.pending += "$";
            }
            state.pos += 1;
            return true;
        }
        // First check for and bypass all properly escaped delimieters
        // This loop will assume that the first leading backtick can not
        // be the first character in state.src, which is known since
        // we have found an opening delimieter already.
        start = state.pos + 1;
        match = start;
        while ((match = state.src.indexOf("$", match)) !== -1) {
            // Found potential $, look for escapes, pos will point to
            // first non escape when complete
            pos = match - 1;
            while (state.src[pos] === "\\") {
                pos -= 1;
            }
            // Even number of escapes, potential closing delimiter found
            if ((match - pos) % 2 == 1) {
                break;
            }
            match += 1;
        }
        // No closing delimter found.  Consume $ and continue.
        if (match === -1) {
            if (!silent) {
                state.pending += "$";
            }
            state.pos = start;
            return true;
        }
        // Check if we have empty content, ie: $$.  Do not parse.
        if (match - start === 0) {
            if (!silent) {
                state.pending += "$$";
            }
            state.pos = start + 1;
            return true;
        }
        // Check for valid closing delimiter
        res = this._isValidDelim(state, match);
        if (!res.can_close) {
            if (!silent) {
                state.pending += "$";
            }
            state.pos = start;
            return true;
        }
        if (!silent) {
            token = state.push("math_inline", "math", 0);
            token.markup = "$";
            token.content = state.src.slice(start, match);
        }
        state.pos = match + 1;
        return true;
    };

    private _mathBlock = (state: IState, start: number, end: number, silent: boolean): boolean => {
        let firstLine,
            lastLine,
            next,
            lastPos,
            found = false,
            token,
            pos = state.bMarks[start] + state.tShift[start],
            max = state.eMarks[start];
        if (pos + 2 > max) {
            return false;
        }
        if (state.src.slice(pos, pos + 2) !== "$$") {
            return false;
        }
        pos += 2;
        firstLine = state.src.slice(pos, max);
        if (silent) {
            return true;
        }
        if (firstLine.trim().slice(-2) === "$$") {
            // Single line expression
            firstLine = firstLine.trim().slice(0, -2);
            found = true;
        }
        for (next = start; !found; ) {
            next++;
            if (next >= end) {
                break;
            }
            pos = state.bMarks[next] + state.tShift[next];
            max = state.eMarks[next];
            if (pos < max && state.tShift[next] < state.blkIndent) {
                // non-empty line with negative indent should stop the list:
                break;
            }
            if (
                state.src
                    .slice(pos, max)
                    .trim()
                    .slice(-2) === "$$"
            ) {
                lastPos = state.src.slice(0, max).lastIndexOf("$$");
                lastLine = state.src.slice(pos, lastPos);
                found = true;
            }
        }
        state.line = next + 1;
        token = state.push("math_block", "math", 0);
        token.block = true;
        token.content =
            (firstLine && firstLine.trim() ? firstLine + "\n" : "") +
            state.getLines(start + 1, next, state.tShift[start], true) +
            (lastLine && lastLine.trim() ? lastLine : "");
        token.map = [start, state.line];
        token.markup = "$$";
        return true;
    };
}
